import { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import MenuList from '../../components/RestaurantMenuPage/MenuList/MenuList';
import { ApiResponse } from '../../interfaces/ApiResponse';
import { Category, CategoryItem } from '../../interfaces/Catalogue';
import { catalogueApi } from '../../services/catalogueApi';
import { RootState } from '../../store/rootState';
import AppNavbar from '../../components/shared/AppNavbar/AppNavbar';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import './CategoryPage.scss';
import Footer from '../../components/shared/Footer/Footer';

interface CategoryPageLocationState {
  category?: Category;
}

const CategoryPage = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { category } = location.state as CategoryPageLocationState || {};
  const restaurantToken = localStorage.getItem("establishementToken");


  // Redux selectors
  const selectedRestaurant = useSelector(
    (state: RootState) => state.shop.selectedRestaurant
  );
  const selectedMode = useSelector(
    (state: RootState) => state.app.selectedMode
  );

  // Local state
  const [restaurantFilter, setRestaurantFilter] = useState("");
  const [categoryItems, setCategoryItems] = useState<CategoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState<boolean>(false);

  // Memoized fetch function
  const fetchCategoryDetails = useCallback(
    async (id: number, azureUrl: string) => {
      try {
        setLoading(true);
        setError(null);

        const response: { data: ApiResponse<CategoryItem[]> } =
          await catalogueApi.getCategoryDetails(id, azureUrl);

        const result = response.data.data.result;

        if (!result || !Array.isArray(result)) {
          throw new Error('Invalid response format');
        }

        const sortedResult = result.sort(
          (a, b) => a.orderIndex - b.orderIndex
        );

        setCategoryItems(sortedResult);
      } catch (err: any) {
        console.error("Error fetching category details:", err);
        setError(err?.message || 'Failed to load category items');
        setCategoryItems([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Effect to fetch category details
  useEffect(() => {
    if (categoryId && selectedRestaurant?.azureUrl && restaurantToken) {
      fetchCategoryDetails(Number(categoryId), selectedRestaurant.azureUrl);
    }

  }, [categoryId, selectedRestaurant?.azureUrl, fetchCategoryDetails, restaurantToken]);

  // Navigation handlers
  const handlePressBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  const handleRetry = useCallback(() => {
    if (categoryId && selectedRestaurant?.azureUrl) {
      fetchCategoryDetails(Number(categoryId), selectedRestaurant.azureUrl);
    }
  }, [categoryId, selectedRestaurant?.azureUrl, fetchCategoryDetails]);

  // Render error state
  if (error && !loading) {
    return (
      <div>
        <AppNavbar
          setRestorentFilter={setRestaurantFilter}
          RestorentFilter={restaurantFilter}
        />
        <PageHeader
          title={category?.designation || "Category"}
          subtitle
          backAction={handlePressBack}
        />
        <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Ressayez
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppNavbar
        setRestorentFilter={setRestaurantFilter}
        RestorentFilter={restaurantFilter}
      />

      <PageHeader
        title={category?.designation || "Categorie"}
        subtitle={category?.description}
        backAction={handlePressBack}
      />

      {/* Category Image Banner */}

      <div className="category-image-banner">
        <div className={`category-image-wrapper ${imageLoaded ? 'loaded' : ''}`}>
          {category?.imageUrl && (
            <img
              src={category.imageUrl}
              alt={category.designation || "Category"}
              className="category-image-banner-img"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}

          {/* Add this designation overlay */}
          <div className="category-designation">
            {category?.designation && (
              <h1 className="category-name">{category.designation}</h1>
            )}
            {category?.description && (
              <p className="category-description">{category.description}</p>

            )}
            {/* <p className="category-description">Lorem ipsum dolor sit amet consectetur adipisicing elit. Tenetur, cupiditate deserunt itaque omnis similique beatae voluptatibus corrupti in cumque repellat quidem placeat, ex dignissimos. Et illo modi nobis libero ab.</p> */}
          </div>
        </div>
      </div>

      {/*  */}

      <MenuList
        loading={loading}
        categoryItems={categoryItems}
        categoryId={Number(categoryId) || 0}
        restaurantLogo={selectedRestaurant?.imageUrl || ""}
        userLoyalityPoints={0}
        restaurantId={selectedRestaurant?.id}
        // page="category"
      />

      <div style={{ height: '80px' }}></div>
      <Footer />
    </div>
  );
};

export default CategoryPage;
